import { toast } from "sonner"
import posthog from 'posthog-js'
import { handleError } from "@/services/errorHandler.js"
import { useAuthContext } from "@/context-state/AuthContext.jsx"
import { useNavigate, useSearchParams } from "react-router-dom"
import { BASE_PATH_AFTER_SUCCESSFUL_AUTH, MCP_CONNECT_RETURN_URL_KEY, MCP_CONNECTOR_SOURCE } from "@/consts/index.js"
import { authUser, googleAuth } from "@/services/api.js"
import useIsMobile from "@/hooks/useIsMobile"

// First-touch attribution cookie written by the marketing site on first landing
// (scoped to .upspring.ai so it's readable here on app.upspring.ai). Best-effort:
// returns {} on any access/parse error.
const readAttrCookie = () => {
    try {
        const raw = document.cookie.split('; ').find(c => c.startsWith('us_attr='))
        if (!raw) {
            return {}
        }
        return JSON.parse(decodeURIComponent(raw.slice('us_attr='.length))) || {}
    } catch {
        return {}
    }
}

// posthog-js packs first-touch as $initial_person_info = { r: referrer, u: landing_url }.
// The individual $initial_utm_* / $initial_referrer keys are NOT stored client-side
// in our posthog-js version (they're only unpacked server-side at ingestion), so we
// unpack here: referrer = r, and utm_* come from parsing the landing_url's query.
// Because persistence is cookie + cross_subdomain, this value carries the real
// marketing first-touch across the www -> app hop.
const readPosthogInitial = () => {
    let info = {}
    try {
        info = posthog?.get_property?.('$initial_person_info') || {}
    } catch {
        info = {}
    }
    const referrer = info.r && info.r !== '$direct' ? info.r : null
    let utm = {}
    try {
        if (info.u) {
            utm = Object.fromEntries(new URL(info.u).searchParams)
        }
    } catch {
        utm = {}
    }
    return { referrer, landing_url: info.u || null, utm }
}

const domainOf = (url) => {
    try {
        return url && url !== '$direct' ? new URL(url).hostname : null
    } catch {
        return null
    }
}

export const useRegister = ({ setIsLoading, setShowMobileOverlay }) => {
    const { authenticate } = useAuthContext()
    const navigate = useNavigate()
    const isMobile = useIsMobile()
    const [searchParams] = useSearchParams()

    // Build the attribution payload at submit time (freshest posthog value).
    // Per-field priority for UTMs: an explicit query param on the current URL
    // (a real link at registration) wins, then posthog's remembered first-touch,
    // then the marketing-site cookie fallback.
    const buildUtmData = () => {
        const ph = readPosthogInitial()
        const cookie = readAttrCookie()

        const pickUtm = (key) => searchParams.get(key) || ph.utm[key] || cookie[key] || null

        // Prefer an explicit URL u_source (a real marketing link). Otherwise, if
        // this registration is happening inside the MCP connector consent flow,
        // attribute it — the stashed return URL (set by McpConnectPage) is the
        // reliable signal. try/catch: some browsers throw on sessionStorage access.
        const urlUSource = searchParams.get('u_source')
        let u_source = urlUSource
        try {
            if (!u_source && sessionStorage.getItem(MCP_CONNECT_RETURN_URL_KEY)) {
                u_source = MCP_CONNECTOR_SOURCE
            }
        } catch {
            // Keep the URL-derived value on any sessionStorage error.
        }

        // The meaningful first-touch referrer/landing: posthog, then the marketing
        // cookie, then the register-page referrer as a last resort. registerPageReferrer
        // is kept as the existing `referrer` field for backward compatibility.
        const registerPageReferrer = document.referrer || null
        const initial_referrer = ph.referrer || cookie.referrer || registerPageReferrer || '$direct'
        const landing_url = ph.landing_url || cookie.landing_url || window.location.href

        return {
            utm_source: pickUtm('utm_source'),
            utm_medium: pickUtm('utm_medium'),
            utm_campaign: pickUtm('utm_campaign'),
            utm_content: pickUtm('utm_content'),
            utm_term: pickUtm('utm_term'),
            gclid: pickUtm('gclid'),
            fbclid: pickUtm('fbclid'),
            u_source,
            referrer: registerPageReferrer,
            initial_referrer,
            initial_referring_domain: domainOf(initial_referrer),
            landing_url,
        }
    }

    const registerUser = async (data) => {
        try {
            setIsLoading(true)
            data.email = data.email.toLowerCase()
            data.utmData = buildUtmData()
            const authResponse = await authUser(data)
            const { user, account, space } = authResponse.data
            // Mobile blocker.
            if (isMobile) {
                setShowMobileOverlay(true)
                toast.success('Welcome, To Upspring!')
                return
            }
            await authenticate(user)
            toast.success('Welcome, To Upspring!')
            // New accounts require onboarding — go straight there instead of
            // the default dashboard page, so the user doesn't flash through a
            // dashboard-style full-page loader on the way.
            const base = `/account/${account._id}/space/${space._id}`
            navigate(account?.requiresOnboarding ? `${base}/onboarding` : base)
        } catch (e) {
            console.log(e)
            toast.error(e.message)
            handleError(e)
        } finally {
            setIsLoading(false)
        }
    }

    const handleGoogleLogin = async (credentialResponse) => {
        try {
            setIsLoading(true)
            const idToken = credentialResponse.credential // Google ID token
            const res = await googleAuth({ idToken })
            // Could be either one, not both
            const { existingUser, authProviderData } = res.data

            if (!existingUser && !authProviderData) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            if (existingUser) {
                await authenticate(existingUser)
                toast.success(`Welcome Back, ${existingUser.name}!`)
                navigate(BASE_PATH_AFTER_SUCCESSFUL_AUTH)
                return
            }

            if (authProviderData) {
                await registerUser({
                    email: authProviderData.email,
                    name: authProviderData.name,
                    password: authProviderData.sub,
                    profileImage: authProviderData.picture,
                    // Add the auth provider data
                    authProvider: 'google',
                    authProviderData: {
                        google: {
                            sub: authProviderData.sub,
                        }
                    }
                })
                return
            }

            console.log(res)
        } catch (e) {
            toast.error(e.message)
            handleError(e)
        } finally {
            setIsLoading(false)
        }
    }

    const handleGoogleLoginError = (error) => {
        handleError(error)
        toast.error(`Google Login Failed - ${error.message}`)
    }


    return { registerUser, handleGoogleLogin, handleGoogleLoginError }

}
